// =====================================================
// PAGE: CrewDetailPage
// Description: Page dédiée pour voir et éditer les détails d'un équipage
// =====================================================

import { Globe, Hash, Phone, Tag, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BedAnswersCard } from '../components/bed/BedAnswersCard';
import { BedEngagementCard } from '../components/bed/BedEngagementCard';
import { AssignUserButton } from '../components/contact';
import { CrewInfoForm, CrewMembersCard } from '../components/crew';
import { FilesList } from '../components/files';
import { CreateTaskModal, LogCallModal, SendEmailModal } from '../components/modals';
import { DetailPageLayout } from '../components/page';
import { PaymentSection } from '../components/payment/PaymentSection';
import { Timeline } from '../components/timeline';
import { Badge, ConfirmDialog, toast } from '../components/ui';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCrewActions } from '../hooks/useCrewActions';
import { usePermissions } from '../hooks/usePermissions';
import { ProbabilityDisplay } from '../lib/crew-probability';
import { deleteCrewErrorMessage } from '../lib/crewDeleteErrors';
import { formatTimeAgo } from '../lib/date-utils';
import { ROUTES } from '../lib/routes/constants';
import type { Crew, UpdateCrewInput } from '../types/crew';
import { CREW_STATUS_COLORS, CREW_STATUS_LABELS } from '../types/crew';

// Configuration des onglets
const TABS = [
  { id: 'globale', label: 'Globale' },
  // Recette 4L du 22/09 (retour 29) : « nous souhaitons avoir la possibilité de
  // consulter TOUTES les données du BED dans la fiche équipe ». La carte
  // existait déjà, repliée en bas de la colonne — personne ne l'avait vue. Un
  // onglet se trouve sans qu'on l'explique.
  { id: 'bulletin', label: "Bulletin d'engagement" },
  { id: 'fichiers', label: 'Fichiers' },
];

export function CrewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ✅ Hook optimisé : pas de chargement automatique de tous les crews
  const { getCrewById, updateCrew, deleteCrew, unassignUser } = useCrewActions();
  const { canManageAssignments, canDeleteEntity, canEditCrewSensitiveFields, canSendEmails } =
    usePermissions();

  const [crew, setCrew] = useState<Crew | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('globale');
  // Revue du 23/09 (point 2) : `get_crew_bed_answers` refuse le rôle `user`
  // (00253) — même règle que BedEngagementCard, sinon chaque visite de l'onglet
  // afficherait « Vous n'avez pas les droits sur la zone BED ».
  const canSeeBedAnswers = canEditCrewSensitiveFields();
  const tabs = useMemo(
    () => (canSeeBedAnswers ? TABS : TABS.filter((tab) => tab.id !== 'bulletin')),
    [canSeeBedAnswers]
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const submitFormRef = useRef<(() => void) | null>(null);
  const [isLogCallModalOpen, setIsLogCallModalOpen] = useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  // ✅ Contact sélectionné pour l'appel/email depuis la card membre
  const [selectedMemberContactId, setSelectedMemberContactId] = useState<string | null>(null);

  // ✅ OPTIMISATION : Ne plus appeler useTimeline, on récupère les events du Timeline
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);
  const refreshTimelineRef = useRef<(() => Promise<void>) | null>(null);

  // Callbacks stables pour Timeline
  const handleEventsLoaded = useCallback((events: any[]) => {
    setTimelineEvents(events);
  }, []);

  const handleRefreshReady = useCallback((fn: () => Promise<void>) => {
    refreshTimelineRef.current = fn;
  }, []);

  // Trouver le dernier appel
  const lastCall = useMemo(() => {
    const callEvents = timelineEvents.filter((event) => event.event_type === 'call_logged');
    return callEvents.length > 0 ? callEvents[0] : null; // Déjà triés par created_at DESC
  }, [timelineEvents]);

  // Charger l'équipage
  const loadCrew = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    const data = await getCrewById(id);
    setCrew(data);
    setLoading(false);
  }, [id, getCrewById]);

  useEffect(() => {
    loadCrew();
  }, [loadCrew]);

  // Gérer la mise à jour
  async function handleUpdate(data: UpdateCrewInput): Promise<boolean> {
    if (!crew) return false;

    const result = await updateCrew(crew.id, data);
    if (result === 'failed') {
      toast.error('Erreur lors de la mise à jour');
      return false;
    }
    if (result === 'partial') {
      toast.error(
        "Équipage enregistré, mais la dérogation n'a pas pu être appliquée aux membres. Réappliquez votre choix puis enregistrez."
      );
    } else {
      toast.success('Équipage mis à jour avec succès');
    }
    // Recharger les données : en sauvegarde partielle, la fiche montre l'état réel
    const updatedCrew = await getCrewById(crew.id);
    setCrew(updatedCrew);
    // Rafraîchir la timeline pour refléter les changements
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
    return result === 'saved';
  }

  // Gérer l'assignation d'un utilisateur
  async function handleAssignUser(userId: string | null): Promise<void> {
    if (!crew) return;

    const result = await updateCrew(crew.id, { assigned_to: userId || undefined });
    if (result !== 'failed') {
      toast.success('Utilisateur assigné avec succès');
      // Recharger les données
      const updatedCrew = await getCrewById(crew.id);
      setCrew(updatedCrew);
      // Rafraîchir la timeline pour refléter le changement
      if (refreshTimelineRef.current) await refreshTimelineRef.current();
    } else {
      toast.error("Erreur lors de l'assignation");
    }
  }

  // Gérer le retrait d'assignation
  async function handleUnassignUser(): Promise<void> {
    if (!crew) return;

    const success = await unassignUser(crew.id);
    if (success) {
      toast.success('Assignation retirée');
      // Recharger les données
      const updatedCrew = await getCrewById(crew.id);
      setCrew(updatedCrew);
      // Rafraîchir la timeline pour refléter le changement
      if (refreshTimelineRef.current) await refreshTimelineRef.current();
    } else {
      toast.error("Erreur lors du retrait de l'assignation");
    }
  }

  // Handler succès modal appel
  async function handleCallSuccess() {
    // Petit délai pour s'assurer que la transaction DB est terminée
    await new Promise((resolve) => setTimeout(resolve, 150));
    // Rafraîchir les événements de timeline pour mettre à jour le dernier appel
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Handler succès modal email
  async function handleEmailSuccess() {
    // Rafraîchir les événements de timeline
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Handler succès modal tâche
  async function handleTaskSuccess() {
    // Rafraîchir les événements de timeline
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Handler zone BED : une levée manuelle ou un override de statut change
  // `crews.status` côté serveur — on recharge la fiche (badge d'en-tête) et la
  // timeline (trace horodatée de la transition).
  async function handleBedStatusChanged() {
    if (!crew) return;

    const updatedCrew = await getCrewById(crew.id);
    setCrew(updatedCrew);
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Handler confirmation paiement BPI
  async function handlePaymentConfirmed() {
    if (!crew) return;

    const updatedCrew = await getCrewById(crew.id);
    setCrew(updatedCrew);
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Gérer la suppression
  async function handleDelete() {
    if (!crew) return;

    try {
      await deleteCrew(crew.id);
      toast.success('Équipage supprimé avec succès');
      navigate(ROUTES.DATA.CREWS.LIST);
    } catch (err) {
      toast.error(deleteCrewErrorMessage(err));
    } finally {
      setShowDeleteConfirm(false);
    }
  }

  // Obtenir l'heure locale
  function getLocalTime() {
    const now = new Date();
    return (
      now.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      }) + ' heure locale'
    );
  }

  // État de chargement
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
          <p className="text-sm text-zinc-600">Chargement de l'équipage...</p>
        </div>
      </div>
    );
  }

  // Équipage non trouvé
  if (!crew) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-lg text-zinc-600">Équipage non trouvé</p>
        <Button onClick={() => navigate(ROUTES.DATA.CREWS.LIST)} variant="outline">
          Retour à la liste
        </Button>
      </div>
    );
  }

  // Nom d'affichage de l'équipage (fallback si pas de nom)
  const crewDisplayName =
    crew.name || (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom');

  // Breadcrumbs pour le header
  const breadcrumbs = [
    { label: 'Data', href: ROUTES.DATA.CREWS.LIST },
    { label: 'Équipages', href: ROUTES.DATA.CREWS.LIST },
    { label: crewDisplayName },
  ];

  // Sous-titre avec badges et infos
  const subtitle = (
    <>
      {/* Numéro d'équipage */}
      {crew.crew_number && (
        <>
          <div className="flex items-center gap-1">
            <Hash className="h-4 w-4" />
            <span>{crew.crew_number}</span>
          </div>
          <div className="h-4 w-px bg-zinc-200" />
        </>
      )}
      {/* Badge de statut */}
      <Badge
        className={`flex items-center gap-1 ${CREW_STATUS_COLORS[crew.status].bg} ${CREW_STATUS_COLORS[crew.status].text} border ${CREW_STATUS_COLORS[crew.status].border}`}
      >
        <Tag className="h-3 w-3" />
        {CREW_STATUS_LABELS[crew.status]}
      </Badge>
      {/* Probabilité */}
      <div className="h-4 w-px bg-zinc-200" />
      <ProbabilityDisplay probability={crew.probability} />
      {/* Dernier appel (si existe) */}
      {lastCall && (
        <>
          <div className="h-4 w-px bg-zinc-200" />
          <div className="flex items-center gap-1">
            <Phone className="h-4 w-4" />
            <span>Dernier appel : {formatTimeAgo(lastCall.created_at)}</span>
          </div>
        </>
      )}
      {/* Heure locale */}
      <div className="h-4 w-px bg-zinc-200" />
      <div className="flex items-center gap-1">
        <Globe className="h-4 w-4" />
        <span>{getLocalTime()}</span>
      </div>
    </>
  );

  // Boutons d'action
  const actions = (
    <>
      {/* Bouton d'assignation visible uniquement pour admin et superadmin */}
      {canManageAssignments() && (
        <AssignUserButton
          assignedUserId={crew.assigned_to}
          assignedUserName={crew.assigned_user?.full_name || crew.assigned_user?.email || null}
          onAssign={handleAssignUser}
          onUnassign={handleUnassignUser}
        />
      )}
      <Button variant="outline" size="sm" onClick={() => setIsCreateTaskModalOpen(true)}>
        Créer une tâche
      </Button>
      {canDeleteEntity('crew') && crew?.status === 'archived' && (
        <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(true)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </>
  );

  return (
    <>
      <DetailPageLayout
        breadcrumbs={breadcrumbs}
        title={crewDisplayName}
        subtitle={subtitle}
        actions={actions}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {/* Contenu onglet Globale */}
        {activeTab === 'globale' && (
          <div className="flex-1 overflow-auto p-6">
            <div className="flex gap-6 h-full">
              {/* Colonne gauche */}
              <div className="flex-1 flex flex-col gap-6">
                {/* Informations générales */}
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-semibold text-zinc-900">Informations générales</p>
                    {isFormDirty && (
                      <Button onClick={() => submitFormRef.current?.()} size="sm">
                        Enregistrer
                      </Button>
                    )}
                  </div>
                  <CrewInfoForm
                    crew={crew}
                    onUpdate={handleUpdate}
                    onDirtyChange={setIsFormDirty}
                    onSubmitRef={(fn) => {
                      submitFormRef.current = fn;
                    }}
                  />
                </Card>

                {/* Paiement (BPI lie au crew_id, alloue des l'intent depuis PAY-10) */}
                <PaymentSection
                  crewId={crew.id}
                  contactName={crewDisplayName}
                  onPaymentConfirmed={handlePaymentConfirmed}
                  // (00242) Le pont crew_members : une attestation « BPI réglé »
                  // posée sur un membre débloque le BED de l'équipage côté
                  // serveur — la fiche doit la voir, et dire d'où elle vient.
                  crewMembers={crew.members}
                  // (00242) Édition de l'équipage : borne la lecture des
                  // attestations à la bonne année (un pilote peut être attesté
                  // sur 2026 ET 2027).
                  productId={crew.product_id}
                />

                {/* Bulletin d'engagement : statut BED, éléments manquants,
                    levée manuelle, override de statut, lien du formulaire
                    (migration 00232, cahier L15-16 / L71-76) */}
                <BedEngagementCard
                  crewId={crew.id}
                  crewName={crewDisplayName}
                  onStatusChanged={handleBedStatusChanged}
                  // TICKET-DEROG-04 : la case « Dérogation validée » des cartes
                  // membres relance la lecture du blocage du solde.
                  derogationKey={(crew.members ?? [])
                    .map(
                      (m) =>
                        `${m.id}:${m.role ?? ''}:${m.birth_date ?? ''}:${m.derogation_validated ? 1 : 0}`
                    )
                    .join('|')}
                />
              </div>

              {/* Colonne droite */}
              <div className="w-[38.25rem] flex flex-col gap-6">
                {/* Contact rapide */}
                <CrewMembersCard
                  crew={crew}
                  onUpdate={loadCrew}
                  // TICKET-SEC-07 : sans droit d'envoi CRM, le bouton ouvre la messagerie (mailto).
                  onEmailClick={
                    canSendEmails()
                      ? (contactId) => {
                          setSelectedMemberContactId(contactId);
                          setIsSendEmailModalOpen(true);
                        }
                      : undefined
                  }
                  onCallClick={(contactId) => {
                    setSelectedMemberContactId(contactId);
                    setIsLogCallModalOpen(true);
                  }}
                />

                {/* Timeline */}
                <Timeline
                  entityType="crew"
                  entityId={crew.id}
                  onEventsLoaded={handleEventsLoaded}
                  onRefreshReady={handleRefreshReady}
                />
              </div>
            </div>
          </div>
        )}

        {/* Contenu onglet Bulletin d'engagement (réservé aux rôles BED) */}
        {activeTab === 'bulletin' && canSeeBedAnswers && (
          <div className="flex-1 overflow-auto p-6">
            <BedAnswersCard crewId={crew.id} defaultOpen />
          </div>
        )}

        {/* Contenu onglet Fichiers */}

        {activeTab === 'fichiers' && (
          <div className="flex-1 overflow-auto p-6">
            <FilesList entityType="crew" entityId={crew.id} />
          </div>
        )}
      </DetailPageLayout>

      {/* Confirmation de suppression */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        title="Supprimer l'équipage archivé"
        description={`Êtes-vous sûr de vouloir supprimer définitivement l'équipage "${crewDisplayName}" ? Les dossiers de paiement associés seront également supprimés. Cette action est irréversible.`}
        confirmLabel="Supprimer"
        variant="destructive"
      />

      {/* Modale Consigner un appel */}
      {selectedMemberContactId && (
        <LogCallModal
          isOpen={isLogCallModalOpen}
          onClose={() => {
            setIsLogCallModalOpen(false);
            setSelectedMemberContactId(null);
          }}
          entityType="contact"
          entityId={selectedMemberContactId}
          relatedCrewId={crew.id}
          relatedContactName={
            crew.members?.find((m) => m.id === selectedMemberContactId)
              ? `${crew.members.find((m) => m.id === selectedMemberContactId)?.first_name} ${crew.members.find((m) => m.id === selectedMemberContactId)?.last_name}`
              : undefined
          }
          onSuccess={handleCallSuccess}
        />
      )}

      {/* Modale Envoyer un email */}
      <SendEmailModal
        isOpen={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        entityType="crew"
        entityId={crew.id}
        defaultRecipients={crew.members?.map((member) => member.email).filter(Boolean) || []}
        onSuccess={handleEmailSuccess}
      />

      {/* Modale Créer une tâche */}
      <CreateTaskModal
        isOpen={isCreateTaskModalOpen}
        onClose={() => setIsCreateTaskModalOpen(false)}
        entityType="crew"
        entityId={crew.id}
        onSuccess={handleTaskSuccess}
      />
    </>
  );
}
